'use client'

import React, { createContext, useContext, useState, useCallback } from 'react'
import { Product } from './types'

interface ComparisonContextType {
  comparedProducts: Product[]
  addToComparison: (product: Product) => void
  removeFromComparison: (productId: string) => void
  clearComparison: () => void
  isInComparison: (productId: string) => boolean
  isComparisonOpen: boolean
  setIsComparisonOpen: (open: boolean) => void
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined)

const MAX_COMPARISON_ITEMS = 4

export function ComparisonProvider({ children }: { children: React.ReactNode }) {
  const [comparedProducts, setComparedProducts] = useState<Product[]>([])
  const [isComparisonOpen, setIsComparisonOpen] = useState(false)

  const addToComparison = useCallback((product: Product) => {
    setComparedProducts((prev) => {
      // Check if already in comparison
      if (prev.some(p => p.id === product.id)) {
        return prev
      }
      
      // Check max limit
      if (prev.length >= MAX_COMPARISON_ITEMS) {
        // Optionally show a toast notification here
        return prev
      }
      
      return [...prev, product]
    })
  }, [])

  const removeFromComparison = useCallback((productId: string) => {
    setComparedProducts((prev) => prev.filter(p => p.id !== productId))
  }, [])

  const clearComparison = useCallback(() => {
    setComparedProducts([])
    setIsComparisonOpen(false)
  }, [])

  const isInComparison = useCallback((productId: string) => {
    return comparedProducts.some(p => p.id === productId)
  }, [comparedProducts])

  return (
    <ComparisonContext.Provider
      value={{
        comparedProducts,
        addToComparison,
        removeFromComparison,
        clearComparison,
        isInComparison,
        isComparisonOpen,
        setIsComparisonOpen,
      }}
    >
      {children}
    </ComparisonContext.Provider>
  )
}

export function useComparison() {
  const context = useContext(ComparisonContext)
  if (context === undefined) {
    throw new Error('useComparison must be used within a ComparisonProvider')
  }
  return context
}
